import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Button,
  Text,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Input,
  Spinner,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Field,
  Select,
  Option,
  Dropdown,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Checkbox,
  makeStyles,
} from '@fluentui/react-components';
import {
  Add24Regular,
  Search24Regular,
  Person24Regular,
  DocumentText24Regular,
  MoreHorizontal24Regular,
  Edit24Regular,
  Delete24Regular,
  People24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { assignmentsApi, surveysApi, usersApi, Assignment } from '../services/api.ts';
import PageHeader from '../components/PageHeader.tsx';
import StatCard from '../components/StatCard.tsx';
import StatusBadge from '../components/StatusBadge.tsx';
import EmptyState from '../components/EmptyState.tsx';
import Pagination from '../components/Pagination.tsx';
import ConfirmDialog from '../components/ConfirmDialog.tsx';
import { useToast } from '../components/Toast.tsx';

const MOBILE = '@media (max-width: 768px)';

const useStyles = makeStyles({
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 'var(--space-md)',
    marginBottom: 'var(--space-lg)',
  },
  table: {
    minWidth: '760px',
    [MOBILE]: {
      minWidth: 0,
    },
  },
  hideOnMobile: {
    [MOBILE]: {
      display: 'none',
    },
  },
  nameCell: {
    [MOBILE]: {
      cursor: 'pointer',
    },
  },
  expandHint: {
    display: 'none',
    [MOBILE]: {
      display: 'inline',
      marginLeft: 'var(--space-xs)',
      color: 'var(--color-text-tertiary)',
      fontSize: '12px',
    },
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-lg)',
    zIndex: 1000,
    animationName: { from: { opacity: 0 }, to: { opacity: 1 } },
    animationDuration: '0.18s',
    '@media (min-width: 769px)': {
      display: 'none',
    },
  },
  modalPanel: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    padding: 'var(--space-lg)',
    width: '100%',
    maxWidth: '420px',
    maxHeight: '85vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-md)',
    animationName: {
      from: { opacity: 0, transform: 'scale(0.96)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animationDuration: '0.2s',
    animationTimingFunction: 'ease',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 'var(--space-sm)',
  },
  modalActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-sm)',
  },
  detailMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-xs)',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
  },
  card: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-lg)',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--space-md)',
    flexWrap: 'wrap',
    marginBottom: 'var(--space-md)',
  },
  searchWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    maxWidth: '300px',
  },
  searchIcon: {
    position: 'absolute',
    left: '10px',
    color: 'var(--color-text-tertiary)',
    pointerEvents: 'none',
  },
  infoBlock: {
    padding: 'var(--space-sm) var(--space-md)',
    backgroundColor: 'var(--color-background)',
    borderRadius: 'var(--radius-sm)',
  },
  infoRow: {
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.8',
  },
  checkboxList: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    padding: 'var(--space-sm)',
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
    padding: '4px 0',
  },
});

function useIsMobile(): boolean {
  const query = '(max-width: 768px)';
  const [isMobile, setIsMobile] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export default function Assignments() {
  const s = useStyles();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [mobileAssignment, setMobileAssignment] = useState<Assignment | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  useEffect(() => { setPage(1); }, [searchTerm, pageSize]);
  const isMobile = useIsMobile();
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<number | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);

  const [showManageDialog, setShowManageDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Assignment | null>(null);

  const { data: assignments, isLoading: assignmentsLoading, error: assignmentsError } = useQuery(
    'assignments', () => assignmentsApi.list({ limit: 100 })
  );

  const { data: availableSurveys } = useQuery('available-surveys', () => surveysApi.getAvailableForAssignment());
  const { data: availableUsers, isLoading: usersLoading, refetch: refetchAvailableUsers } = useQuery(
    'available-users', () => usersApi.getAvailableForAssignment({ limit: 100 }),
    { staleTime: 0, cacheTime: 0 }
  );
  const { data: progressSummary } = useQuery('assignment-progress', () => assignmentsApi.getProgressSummary());

  const createAssignmentMutation = useMutation(assignmentsApi.create, {
    onSuccess: () => { queryClient.invalidateQueries('assignments'); queryClient.invalidateQueries('available-users'); setShowAssignDialog(false); setSelectedSurvey(null); toast.success('Assignment created'); },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to create assignment'),
  });

  const createBulkAssignmentMutation = useMutation(assignmentsApi.createBulk, {
    onSuccess: () => { queryClient.invalidateQueries('assignments'); queryClient.invalidateQueries('available-users'); setShowBulkAssignDialog(false); setSelectedSurvey(null); setSelectedUsers([]); toast.success('Assignments created'); },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to create assignments'),
  });

  const updateAssignmentMutation = useMutation(
    ({ id, data }: { id: number; data: { status?: string } }) => assignmentsApi.update(id, data),
    {
      onSuccess: () => { queryClient.invalidateQueries('assignments'); setShowManageDialog(false); setSelectedAssignment(null); toast.success('Assignment updated'); },
      onError: () => toast.error('Failed to update assignment'),
    }
  );

  const deleteAssignmentMutation = useMutation(
    (id: number) => assignmentsApi.delete(id),
    {
      onSuccess: () => { queryClient.invalidateQueries('assignments'); queryClient.invalidateQueries('available-users'); setShowManageDialog(false); setSelectedAssignment(null); toast.success('Assignment deleted'); },
      onError: () => toast.error('Failed to delete assignment'),
    }
  );

  const reassignMutation = useMutation(
    ({ id, new_survey_id }: { id: number; new_survey_id: number }) => assignmentsApi.reassign(id, new_survey_id),
    {
      onSuccess: () => { queryClient.invalidateQueries('assignments'); setShowManageDialog(false); setSelectedAssignment(null); toast.success('Assignment reassigned'); },
      onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to reassign'),
    }
  );

  const handleAssignUser = async () => {
    if (!selectedSurvey || selectedUsers.length === 0) { toast.warning('Please select a survey and at least one user'); return; }
    setIsAssigning(true);
    try {
      if (selectedUsers.length === 1) await createAssignmentMutation.mutateAsync({ user_id: selectedUsers[0], survey_id: selectedSurvey });
      else await createBulkAssignmentMutation.mutateAsync({ user_ids: selectedUsers, survey_id: selectedSurvey });
    } finally { setIsAssigning(false); }
  };

  const handleUpdateAssignmentStatus = async (status: string) => {
    if (!selectedAssignment) return;
    setIsUpdating(true);
    try { await updateAssignmentMutation.mutateAsync({ id: selectedAssignment.id, data: { status } }); }
    finally { setIsUpdating(false); }
  };

  const handleReassign = async (newSurveyId: number) => {
    if (!selectedAssignment) return;
    await reassignMutation.mutateAsync({ id: selectedAssignment.id, new_survey_id: newSurveyId });
  };

  const filteredAssignments = assignments?.filter(a =>
    a.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.survey_title?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  const totalAssignments = filteredAssignments.length;

  // If the current page falls off the end (e.g. after deletes), step back to the new last page.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalAssignments / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [totalAssignments, pageSize, page]);

  const pagedAssignments = filteredAssignments.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <PageHeader
        title="Assignment Management"
        subtitle="Manage user assignments to surveys"
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            <Button appearance="secondary" icon={<People24Regular />} onClick={() => { setShowBulkAssignDialog(true); refetchAvailableUsers(); }}>Bulk Assign</Button>
            <Button appearance="primary" icon={<Add24Regular />} onClick={() => { setShowAssignDialog(true); refetchAvailableUsers(); }}>Assign User</Button>
          </div>
        }
      />

      {/* Stats */}
      {progressSummary && (
        <div className={s.statsGrid}>
          <StatCard title="Total Assignments" value={progressSummary.total} />
          <StatCard title="Completed" value={progressSummary.completed} color="var(--color-success)" />
          <StatCard title="In Progress" value={progressSummary.in_progress} color="#946800" />
          <StatCard title="Completion Rate" value={`${progressSummary.completion_rate}%`} />
        </div>
      )}

      {/* Table */}
      <div className={s.card}>
        <div className={s.toolbar}>
          <div className={s.searchWrap}>
            <Search24Regular className={s.searchIcon} />
            <Input placeholder="Search assignments..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '36px', width: '100%' }} />
          </div>
        </div>

        {assignmentsLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' }}><Spinner size="large" /></div>
        ) : assignmentsError ? (
          <EmptyState title="Error loading assignments" description="Please try again later" />
        ) : filteredAssignments.length === 0 ? (
          <EmptyState
            icon={<DocumentText24Regular />}
            title={searchTerm ? 'No assignments found' : 'No assignments yet'}
            description={searchTerm ? 'Try a different search term' : 'Assign users to surveys to get started'}
          />
        ) : (
          <>
          <div className="table-scroll">
          <Table className={s.table}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>User</TableHeaderCell>
                <TableHeaderCell>Survey</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile}>Assigned</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile}>Completed</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile} style={{ width: '80px' }}>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedAssignments.map((assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell
                    className={s.nameCell}
                    onClick={isMobile ? () => setMobileAssignment(assignment) : undefined}
                  >
                    <Text weight="medium" size={300}>{assignment.user_name}</Text>
                    <span className={s.expandHint}>›</span>
                  </TableCell>
                  <TableCell><Text size={300}>{assignment.survey_title}</Text></TableCell>
                  <TableCell><StatusBadge status={assignment.status} /></TableCell>
                  <TableCell className={s.hideOnMobile}><Text size={300}>{assignment.assigned_at ? new Date(assignment.assigned_at).toLocaleDateString() : 'N/A'}</Text></TableCell>
                  <TableCell className={s.hideOnMobile}><Text size={300}>{assignment.completed_at ? new Date(assignment.completed_at).toLocaleDateString() : '-'}</Text></TableCell>
                  <TableCell className={s.hideOnMobile}>
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button appearance="subtle" size="small" icon={<MoreHorizontal24Regular />} />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem icon={<Edit24Regular />} onClick={() => { setSelectedAssignment(assignment); setShowManageDialog(true); }}>Manage</MenuItem>
                          <MenuItem icon={<Delete24Regular />} onClick={() => setConfirmDelete(assignment)}>Delete</MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination total={filteredAssignments.length} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }} />
          </>
        )}
      </div>

      {/* Mobile-only assignment details popup */}
      {mobileAssignment && (
        <div className={s.modalOverlay} onClick={() => setMobileAssignment(null)}>
          <div className={s.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <Text weight="semibold" size={400}>{mobileAssignment.user_name}</Text>
              <Button appearance="subtle" size="small" icon={<Dismiss24Regular />} aria-label="Close" onClick={() => setMobileAssignment(null)} />
            </div>
            <div><StatusBadge status={mobileAssignment.status} /></div>
            <div className={s.detailMeta}>
              <span>Survey: {mobileAssignment.survey_title}</span>
              <span>Assigned: {mobileAssignment.assigned_at ? new Date(mobileAssignment.assigned_at).toLocaleDateString() : 'N/A'}</span>
              <span>Completed: {mobileAssignment.completed_at ? new Date(mobileAssignment.completed_at).toLocaleDateString() : '-'}</span>
            </div>
            <div className={s.modalActions}>
              <Button appearance="primary" icon={<Edit24Regular />} onClick={() => { const a = mobileAssignment; setMobileAssignment(null); setSelectedAssignment(a); setShowManageDialog(true); }}>Manage</Button>
              <Button appearance="secondary" icon={<Delete24Regular />} onClick={() => { const a = mobileAssignment; setMobileAssignment(null); setConfirmDelete(a); }} style={{ color: 'var(--color-error)' }}>Delete</Button>
            </div>
          </div>
        </div>
      )}

      {/* Single Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={(_, data) => setShowAssignDialog(data.open)}>
        <DialogSurface style={{ width: '90vw', maxWidth: '480px' }}>
          <DialogBody>
            <DialogTitle>Assign User to Survey</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <Field label="Select Survey" required>
                  <Select style={{ width: '100%' }} value={selectedSurvey?.toString() || ''} onChange={(_, data) => setSelectedSurvey(data.value ? parseInt(data.value) : null)}>
                    <option value="">Choose a survey...</option>
                    {availableSurveys?.map((survey) => (<option key={survey.id} value={survey.id.toString()}>{survey.title} ({survey.status})</option>))}
                  </Select>
                </Field>
                <Field label="Select User" required>
                  {usersLoading ? <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px' }}><Spinner size="small" /><Text size={200}>Loading...</Text></div> : (
                    <Select style={{ width: '100%' }} value={selectedUsers[0]?.toString() || ''} onChange={(_, data) => setSelectedUsers(data.value ? [parseInt(data.value)] : [])}>
                      <option value="">Choose a user...</option>
                      {availableUsers?.map((user) => (<option key={user.id} value={user.id.toString()}>{user.first_name} {user.last_name} ({user.email})</option>))}
                    </Select>
                  )}
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
              <Button appearance="primary" onClick={handleAssignUser} disabled={isAssigning || !selectedSurvey || selectedUsers.length === 0}>{isAssigning ? 'Assigning...' : 'Assign User'}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={showBulkAssignDialog} onOpenChange={(_, data) => setShowBulkAssignDialog(data.open)}>
        <DialogSurface style={{ width: '90vw', maxWidth: '480px' }}>
          <DialogBody>
            <DialogTitle>Bulk Assign Users</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <Field label="Select Survey" required>
                  <Select style={{ width: '100%' }} value={selectedSurvey?.toString() || ''} onChange={(_, data) => setSelectedSurvey(data.value ? parseInt(data.value) : null)}>
                    <option value="">Choose a survey...</option>
                    {availableSurveys?.map((survey) => (<option key={survey.id} value={survey.id.toString()}>{survey.title} ({survey.status})</option>))}
                  </Select>
                </Field>
                <Field label="Select Users" required>
                  <div className={s.checkboxList}>
                    {usersLoading ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: 'var(--space-md)' }}><Spinner size="small" /><Text size={200}>Loading...</Text></div> : (
                      <>
                        {availableUsers?.map((user) => (
                          <div key={user.id} className={s.checkboxItem}>
                            <Checkbox checked={selectedUsers.includes(user.id)} onChange={(_, data) => { if (data.checked) setSelectedUsers([...selectedUsers, user.id]); else setSelectedUsers(selectedUsers.filter(id => id !== user.id)); }} />
                            <Text size={300}>{user.first_name} {user.last_name} ({user.email})</Text>
                          </div>
                        ))}
                        {availableUsers && availableUsers.length === 0 && <Text size={200} style={{ color: 'var(--color-error)', padding: 'var(--space-md)', textAlign: 'center', display: 'block' }}>No users available for assignment.</Text>}
                      </>
                    )}
                  </div>
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
              <Button appearance="primary" onClick={handleAssignUser} disabled={isAssigning || !selectedSurvey || selectedUsers.length === 0}>{isAssigning ? 'Assigning...' : `Assign ${selectedUsers.length} Users`}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Manage Assignment Dialog */}
      <Dialog open={showManageDialog} onOpenChange={(_, data) => setShowManageDialog(data.open)}>
        <DialogSurface style={{ width: '90vw', maxWidth: '480px' }}>
          <DialogBody>
            <DialogTitle>Manage Assignment</DialogTitle>
            <DialogContent>
              {selectedAssignment && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                  <div className={s.infoBlock}>
                    <div className={s.infoRow}>User: {selectedAssignment.user_name}</div>
                    <div className={s.infoRow}>Survey: {selectedAssignment.survey_title}</div>
                    <div className={s.infoRow}>Status: {selectedAssignment.status}</div>
                    <div className={s.infoRow}>Assigned: {new Date(selectedAssignment.assigned_at).toLocaleDateString()}</div>
                  </div>
                  <Field label="Update Status">
                    <Dropdown selectedOptions={[selectedAssignment.status]} onOptionSelect={(_, data) => { const v = (data.optionValue as string) || ''; if (v) handleUpdateAssignmentStatus(v); }}>
                      <Option value="assigned">Assigned</Option>
                      <Option value="in_progress">In Progress</Option>
                      <Option value="completed">Completed</Option>
                      <Option value="overdue">Overdue</Option>
                    </Dropdown>
                  </Field>
                  <Field label="Reassign to Different Survey">
                    <Dropdown placeholder="Choose a different survey..." onOptionSelect={(_, data) => { const v = data.optionValue as string; if (v) handleReassign(parseInt(v, 10)); }}>
                      {availableSurveys?.filter(sv => sv.id !== selectedAssignment.survey_id).map(survey => (<Option key={survey.id} value={survey.id.toString()} text={`${survey.title} (${survey.status})`}>{survey.title} ({survey.status})</Option>))}
                    </Dropdown>
                  </Field>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Close</Button></DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Confirm Delete */}
      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => { if (confirmDelete) { await deleteAssignmentMutation.mutateAsync(confirmDelete.id); setConfirmDelete(null); } }}
        title="Delete Assignment"
        message="Are you sure you want to delete this assignment? This action cannot be undone."
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}
